// ─────────────────────────────────────────────────────────────────────────────
// Live / sample toggle, for components that live outside App.js.
//
// App.js has survaiPreviewMode() / setSurvaiPreviewMode() for the same job, and they
// are plain functions in a 57,000-line module rather than exports. Rather than reach
// into that file, this reimplements the two lines — and reimplements them against the
// SAME localStorage key format, which is the only part that has to agree.
//
// The key format is pinned by a test. Two components disagreeing about where the
// toggle is stored would produce a switch that looks like it worked and left the other
// screen on the other dataset — which is the exact bug App.js's own comment describes
// having shipped once already.
// ─────────────────────────────────────────────────────────────────────────────

/** The one thing that must match App.js. */
export const previewKey = (viewKey) => `survai_preview_mode_${viewKey}`;

export function previewMode(viewKey, fallback = true) {
  try {
    const v = localStorage.getItem(previewKey(viewKey));
    if (v === "on") return true;
    if (v === "off") return false;
  } catch (_) {
    /* private window, blocked storage: fall through to the default */
  }
  try {
    const env = process.env.REACT_APP_SURVAI_PREVIEW;
    if (env != null && String(env).trim() !== "") return String(env).toLowerCase() !== "false";
  } catch (_) {}
  return fallback;
}

export function setPreviewMode(viewKey, on) {
  try {
    localStorage.setItem(previewKey(viewKey), on ? "on" : "off");
  } catch (_) {
    /* the toggle still works for this render; it just will not persist */
  }
}

/** The two labelled positions, so which dataset is on screen is readable unpressed. */
export function DataSourceSwitch({ preview, onToggle, note, React: R }) {
  // Takes React as a prop so this module stays plain JS and importable from a test.
  const h = R.createElement;
  const opts = [
    { on: false, label: "Live facility data" },
    { on: true, label: "Sample data" },
  ];
  return h(
    "div",
    { style: { display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" } },
    h(
      "div",
      { style: { display: "inline-flex", borderRadius: 8, overflow: "hidden", border: "1px solid var(--ed-line-hover)" } },
      opts.map((opt) =>
        h(
          "button",
          {
            key: String(opt.on),
            type: "button",
            onClick: () => {
              if (preview !== opt.on) onToggle();
            },
            "aria-pressed": preview === opt.on,
            style: {
              border: "none",
              cursor: preview === opt.on ? "default" : "pointer",
              padding: "6px 13px",
              fontSize: 12,
              fontWeight: 700,
              fontFamily: "inherit",
              background: preview === opt.on ? (opt.on ? "#1d4ed8" : "var(--ed-brand)") : "var(--ed-canvas)",
              color: preview === opt.on ? "var(--ed-canvas)" : "var(--ed-ink-3)",
            },
          },
          opt.label,
        ),
      ),
    ),
    h("span", { style: { fontSize: 11.5, color: "var(--ed-muted)" } }, note),
  );
}

export default previewMode;
